import React, { useContext, useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import Avatar from '@mui/material/Avatar'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined'
import WebOutlinedIcon from '@mui/icons-material/WebOutlined'
import { Auth } from 'aws-amplify'
import { FiUsers } from 'react-icons/fi'
import {
  MdLogout,
  MdOutlineAssignment,
  MdOutlineDescription,
  MdOutlineFolder,
  MdOutlineNotificationsNone,
  MdOutlineQuiz,
  MdLocalHospital
} from 'react-icons/md'

import { UserContext } from '@context/UserContext'
import { CurrentOrganizationContext } from '@context/CurrentOrganization'
import {
  getFirstNameFromName,
  getInitialsFromName,
  getUserDisplayName
} from '@utils/functions'

import styles from './Sidebar.module.scss'

const ICON_SIZE = 22

const Sidebar = () => {
  const { me, organizations } = useContext(UserContext)
  const { handleOrganizationChange, currentOrganization } = useContext(
    CurrentOrganizationContext
  )
  const [isAdmin, setIsAdmin] = useState(false)
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  )

  const displayName = getUserDisplayName(me)
  const welcomeName = getFirstNameFromName(displayName)
  const avatarSrc =
    me?.profile_picture_url ||
    me?.profile_picture ||
    me?.image_url ||
    me?.avatar_url ||
    undefined

  const [avatarError, setAvatarError] = useState(false)

  useEffect(() => {
    setAvatarError(false)
  }, [avatarSrc])

  useEffect(() => {
    const on = () => setOnline(true)
    const off = () => setOnline(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])

  useEffect(() => {
    if (organizations?.length > 0 && !currentOrganization) {
      handleOrganizationChange({ target: { value: organizations[0].id } })
    }
  }, [organizations, currentOrganization, handleOrganizationChange])

  useEffect(() => {
    if (me?.telecom?.length > 0) {
      setIsAdmin(
        me.telecom.some((telecom) => telecom.value.includes('televu.ca'))
      )
    } else if (me?.is_admin) {
      setIsAdmin(true)
    }
  }, [me])

  async function signOut () {
    try {
      await Auth.signOut()
    } catch (error) {
      console.log('error signing out: ', error)
    }
  }

  const navClass = ({ isActive }) =>
    isActive
      ? `${styles.sidebar__item} ${styles['sidebar__item--active']}`
      : styles.sidebar__item

  const isClinicvu = process.env.REACT_APP_environment_type === 'clinicvu'
  const showPortals = isAdmin || me?.is_admin

  return (
    <div className={styles.sidebar}>
      <div className={styles.welcomeSection}>
        <Avatar
          className={styles.welcomeAvatar}
          src={avatarSrc && !avatarError ? avatarSrc : undefined}
          alt={displayName || 'User'}
          imgProps={{
            onError: () => setAvatarError(true)
          }}
        >
          {getInitialsFromName(displayName)}
        </Avatar>
        <div className={styles.welcomeTextRow}>
          <span className={styles.welcomeTextLabel}>Welcome</span>
          <span className={styles.welcomeTextName}>{welcomeName}</span>
        </div>
        <div className={styles.connectionRow}>
          <span className={online ? styles.onlineDot : styles.offlineDot} />
          <span
            className={
              online ? styles.connectionTextOnline : styles.connectionTextOffline
            }
          >
            {online ? 'Connected' : 'Disconnected'}
          </span>
        </div>
        {isClinicvu
          ? (
          <div className={styles.sidebar__portalRow}>
            <div className={styles.sidebar__portalLabel}>Portal:</div>
            {me
              ? (
              <Select
                size="small"
                value={currentOrganization ?? ''}
                onChange={handleOrganizationChange}
              >
                {organizations?.map((organization, i) => {
                  return (
                    <MenuItem key={i} value={organization.id}>
                      {organization.name}
                    </MenuItem>
                  )
                })}
              </Select>
                )
              : null}
          </div>
            )
          : null}
      </div>

      <nav className={styles.sidebar__nav}>
        <NavLink className={navClass} to={isClinicvu ? '/patients' : '/users'}>
          <span className={styles.sidebar__itemInner}>
            {isClinicvu
              ? (
              <LocalHospitalOutlinedIcon
                className={styles.sidebar__itemIcon}
                sx={{ fontSize: ICON_SIZE }}
              />
                )
              : (
              <FiUsers className={styles.sidebar__itemIcon} size={ICON_SIZE} />
                )}
            <span className={styles['sidebar__item-text']}>
              {isClinicvu ? 'Patients' : 'Users'}
            </span>
          </span>
        </NavLink>
        <NavLink className={navClass} to="/alerts">
          <span className={styles.sidebar__itemInner}>
            <MdOutlineNotificationsNone
              className={styles.sidebar__itemIcon}
              size={ICON_SIZE}
            />
            <span className={styles['sidebar__item-text']}>Alerts</span>
          </span>
        </NavLink>
        {isClinicvu
          ? (
          <>
            <NavLink className={navClass} to="/care-plans">
              <span className={styles.sidebar__itemInner}>
                <MdOutlineAssignment
                  className={styles.sidebar__itemIcon}
                  size={ICON_SIZE}
                />
                <span className={styles['sidebar__item-text']}>Care Plans</span>
              </span>
            </NavLink>
            <NavLink className={navClass} to="/questionnaires">
              <span className={styles.sidebar__itemInner}>
                <MdOutlineQuiz className={styles.sidebar__itemIcon} size={ICON_SIZE} />
                <span className={styles['sidebar__item-text']}>Questionnaires</span>
              </span>
            </NavLink>
            <NavLink className={navClass} to="/flow-sheets">
              <span className={styles.sidebar__itemInner}>
                <MdOutlineDescription
                  className={styles.sidebar__itemIcon}
                  size={ICON_SIZE}
                />
                <span className={styles['sidebar__item-text']}>Flow Sheets</span>
              </span>
            </NavLink>
            <NavLink className={navClass} to="/files">
              <span className={styles.sidebar__itemInner}>
                <MdOutlineFolder className={styles.sidebar__itemIcon} size={ICON_SIZE} />
                <span className={styles['sidebar__item-text']}>Files</span>
              </span>
            </NavLink>
            <NavLink className={navClass} to="/clinic">
              <span className={styles.sidebar__itemInner}>
                <MdLocalHospital
                  className={styles.sidebar__itemIcon}
                  size={ICON_SIZE}
                />
                <span className={styles['sidebar__item-text']}>Clinic</span>
              </span>
            </NavLink>
            {showPortals
              ? (
              <NavLink className={navClass} to="/portals">
                <span className={styles.sidebar__itemInner}>
                  <WebOutlinedIcon
                    className={styles.sidebar__itemIcon}
                    sx={{ fontSize: ICON_SIZE }}
                  />
                  <span className={styles['sidebar__item-text']}>Portal</span>
                </span>
              </NavLink>
                )
              : null}
          </>
            )
          : null}
        <button
          type="button"
          key="SignOut"
          onClick={signOut}
          className={`${styles.sidebar__item} ${styles.logout}`}
        >
          <span className={styles.sidebar__itemInner}>
            <MdLogout className={styles.sidebar__itemIconLogout} size={ICON_SIZE} />
            <span className={styles['sidebar__item-text']}>Logout</span>
          </span>
        </button>
      </nav>

      <div className={styles.sidebar__footer}>
        © {new Date().getFullYear()} TeleVU Innovation Ltd. All Rights Reserved
      </div>
    </div>
  )
}

export default Sidebar
